import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublic } from '@repo/types/files';

import { useSearchFileSystem } from '@repo/api/file-system/useSearchFileSystem';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { FileIcon, FolderIcon } from 'lucide-react';
import { useState } from 'react';

import type { SearchGroup } from '@/components/ui/input';

import { CommandSearchInput } from '@/components/ui/input';

interface FsSearchCommandProps {
  placeholder?: string;
  onDirectorySelect: (directory: DirectoryPublic) => void;
  onFileSelect: (file: FilePublic) => void;
  isFileSelected?: (file: FilePublic) => boolean;
  isDirectorySelected?: (directory: DirectoryPublic) => boolean;
  className?: string;
}

export default function FsSearchCommand(fsSearchCommandProps: FsSearchCommandProps) {
  const { placeholder = 'Wpisz nazwę pliku/folderu aby wyszukać...', onDirectorySelect, onFileSelect, isDirectorySelected, isFileSelected, className } = fsSearchCommandProps;
  const [open, onOpenChange] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery: string = useDebounceValue(searchQuery, 400);

  const { data: searchResults, isPending } = useSearchFileSystem(debouncedSearchQuery, open);

  const debounceValuePending: boolean = searchQuery !== debouncedSearchQuery;

  const searchGroups: SearchGroup<DirectoryPublic | FilePublic>[] = [
    {
      key: 'directories',
      label: 'Foldery',
      items: searchResults?.dirs || [],
      renderItem: (directory: DirectoryPublic | FilePublic) => (
        <>
          <FolderIcon />
          <span>{directory.name}</span>
        </>
      ),
      onItemSelect: (item: DirectoryPublic | FilePublic) => {
        const directory = item as DirectoryPublic;
        onDirectorySelect(directory);
      },
      getItemKey: (directory: DirectoryPublic | FilePublic) => directory.id,
      isItemSelected: isDirectorySelected
        ? (item: DirectoryPublic | FilePublic) => {
            const directory = item as DirectoryPublic;
            return isDirectorySelected(directory);
          }
        : undefined,
    },
    {
      key: 'files',
      label: 'Pliki',
      items: searchResults?.files || [],
      renderItem: (file: DirectoryPublic | FilePublic) => (
        <>
          <FileIcon />
          <span>{file.name}</span>
        </>
      ),
      onItemSelect: (item: DirectoryPublic | FilePublic) => {
        const file = item as FilePublic;
        onFileSelect(file);
      },
      getItemKey: (file: DirectoryPublic | FilePublic) => file.id,
      isItemSelected: isFileSelected
        ? (item: DirectoryPublic | FilePublic) => {
            const file = item as FilePublic;
            return isFileSelected(file);
          }
        : undefined,
    },
  ];

  return (
    <CommandSearchInput
      placeholder={placeholder}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      groups={searchGroups}
      isLoading={debounceValuePending || isPending}
      emptyMessage="Nie znaleziono wyników dla"
      loadingMessage="Ładowanie..."
      className={className}
      openCommand={open}
      onOpenCommandChange={onOpenChange}
    />
  );
}
